import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";
import { X, Globe2, Lock, Upload } from "lucide-react";

const CATEGORIES = [
  { key: "music", label: "Music Artist" },
  { key: "models", label: "Model" },
  { key: "athletes", label: "Athlete" },
  { key: "business", label: "Business Owner" },
  { key: "film", label: "Film & Entertainment" },
];

function fileToDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function EditProfileModal({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", headline: "", bio: "", category: "music", location: "",
    email: "", phone: "", instagram: "", twitter: "", website: "",
    booking_url: "", booking_policy: "", avatar: "", cover_image: "",
  });
  const [saving, setSaving] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [currentIsPublic, setCurrentIsPublic] = useState(true);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!profile) return;
    setForm({
      name: profile.name || "",
      headline: profile.headline || "",
      bio: profile.bio || "",
      category: profile.category || "music",
      location: profile.location || "",
      email: profile.email || "",
      phone: profile.phone || "",
      instagram: profile.instagram || "",
      twitter: profile.twitter || "",
      website: profile.website || "",
      booking_url: profile.booking_url || "",
      booking_policy: profile.booking_policy || "",
      avatar: profile.avatar || "",
      cover_image: profile.cover_image || "",
    });
    setCurrentIsPublic(profile.is_public !== false);
    api.get("/community/me/subscription").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, [profile]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/profiles/${profile.profile_id}`, form);
      toast.success("Profile updated.");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async () => {
    const goingPublic = !currentIsPublic;
    if (goingPublic && sub && !sub.is_active) {
      // No active subscription — redirect to join page
      window.location.href = "/community/join";
      return;
    }
    setTogglingVisibility(true);
    try {
      const { data } = await api.put(`/profiles/${profile.profile_id}/visibility`, { is_public: goingPublic });
      setCurrentIsPublic(data.is_public);
      onSaved?.({ ...profile, is_public: data.is_public });
      toast.success(data.is_public ? "Profile is now PUBLIC — visible in Community." : "Profile is now PRIVATE — hidden from Community.");
    } catch (err) {
      if (err.response?.status === 402) {
        toast.message("Subscription required to make profile public.");
        window.location.href = "/community/join";
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setTogglingVisibility(false);
    }
  };

  if (!profile) return null;

  const field = (k, label, opts = {}) => (
    <div>
      <label className="label text-white/70 mb-2 block">{label}</label>
      {opts.textarea ? (
        <textarea
          rows={opts.rows || 3} maxLength={opts.max || 800}
          value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none resize-none"
          data-testid={`edit-profile-${k}`}
        />
      ) : (
        <input
          type={opts.type || "text"} maxLength={opts.max || 200}
          value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          placeholder={opts.placeholder || ""}
          className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
          data-testid={`edit-profile-${k}`}
        />
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-ink-900/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
      data-testid="edit-profile-modal"
    >
      <div
        className="bg-ink-800 border border-pfc-gold/30 w-full max-w-2xl max-h-[92vh] overflow-y-auto p-8 lg:p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => !saving && onClose()}
          className="absolute top-4 right-4 text-white/60 hover:text-white p-2"
          data-testid="edit-profile-close"
        >
          <X size={20} />
        </button>
        <div className="label text-pfc-gold mb-2">Profile</div>
        <h3 className="font-display text-3xl text-white uppercase leading-tight mb-3">
          Edit Your <span className="gold-text">Profile</span>
        </h3>
        <p className="text-white/55 font-body text-sm mb-6">Editing is free for everyone.</p>

        {/* VISIBILITY TOGGLE */}
        <div
          className={`mb-6 p-5 border ${currentIsPublic ? "border-pfc-gold/40 bg-pfc-gold/5" : "border-white/15 bg-ink-900"} flex items-start gap-4 flex-wrap`}
          data-testid="edit-profile-visibility-panel"
        >
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2 mb-1">
              {currentIsPublic ? <Globe2 size={16} className="text-pfc-gold" /> : <Lock size={16} className="text-white/60" />}
              <span className="font-display text-white text-base uppercase">
                {currentIsPublic ? "Profile is Public" : "Profile is Private"}
              </span>
            </div>
            <p className="text-white/60 text-xs font-body">
              {currentIsPublic
                ? "Visible to everyone in the Community directory and on PFC."
                : "Hidden from Community. Pay $0.99/mo or $9.99/yr to make it public."}
            </p>
          </div>
          {currentIsPublic ? (
            <button
              type="button"
              onClick={toggleVisibility}
              disabled={togglingVisibility}
              className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 border border-white/20 text-white hover:border-pfc-red hover:text-pfc-red transition disabled:opacity-50"
              data-testid="edit-profile-make-private"
            >
              {togglingVisibility ? "…" : "Make Private"}
            </button>
          ) : sub?.is_active ? (
            <button
              type="button"
              onClick={toggleVisibility}
              disabled={togglingVisibility}
              className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
              data-testid="edit-profile-make-public"
            >
              {togglingVisibility ? "…" : "Make Public"}
            </button>
          ) : (
            <Link
              to="/community/join"
              className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition"
              data-testid="edit-profile-go-subscribe"
            >
              Subscribe to Go Public →
            </Link>
          )}
        </div>

        <form onSubmit={save} className="space-y-4" data-testid="edit-profile-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("name", "Display Name")}
            <div>
              <label className="label text-white/70 mb-2 block">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-ink-900 border border-white/15 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
                data-testid="edit-profile-category"
              >
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
          {field("headline", "Headline", { placeholder: "DJ + Producer · Atlanta" })}
          {field("bio", "Bio", { textarea: true, rows: 4, max: 1500 })}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("location", "Location")}
            {field("phone", "Phone / Contact")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("email", "Contact Email", { type: "email" })}
            {field("website", "Website", { placeholder: "https://" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("instagram", "Instagram", { placeholder: "@handle" })}
            {field("twitter", "Twitter / X", { placeholder: "@handle" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ImageUploadField
              label="Avatar"
              value={form.avatar}
              maxBytes={3 * 1024 * 1024}
              onChange={(v) => setForm({ ...form, avatar: v })}
              testid="edit-profile-avatar"
              previewClass="w-24 h-24 object-cover rounded-full border border-pfc-gold/40"
            />
            <ImageUploadField
              label="Cover Image"
              value={form.cover_image}
              maxBytes={5 * 1024 * 1024}
              onChange={(v) => setForm({ ...form, cover_image: v })}
              testid="edit-profile-cover"
              previewClass="w-full h-24 object-cover rounded border border-pfc-gold/40"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("booking_url", "Booking URL", { placeholder: "https://" })}
            {field("booking_policy", "Booking Policy", { textarea: true, rows: 2, max: 400 })}
          </div>

          <button
            type="submit" disabled={saving}
            className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50 mt-4"
            data-testid="edit-profile-save"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}


function ImageUploadField({ label, value, onChange, maxBytes, testid, previewClass }) {
  const [busy, setBusy] = useState(false);
  const isData = value && value.startsWith("data:");
  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (f.size > maxBytes) {
      toast.error(`Image must be under ${Math.round(maxBytes / 1024 / 1024)}MB.`);
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToDataURL(f);
      onChange(dataUrl);
    } catch {
      toast.error("Could not read that image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`${testid}-field`}>
      <label className="label text-white/70 mb-2 block">{label}</label>
      {value && (
        <div className="mb-2 flex items-center gap-3">
          <img src={value} alt={label} className={previewClass} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="font-sub uppercase tracking-[0.2em] text-[10px] text-pfc-red hover:underline"
            data-testid={`${testid}-remove`}
          >
            Remove
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="cursor-pointer font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-white/15 text-white/80 hover:border-pfc-gold hover:text-pfc-gold transition inline-flex items-center gap-1.5">
          <Upload size={12} />
          {busy ? "Reading…" : isData ? "Replace File" : "Upload File"}
          <input type="file" accept="image/*" hidden onChange={handlePick} data-testid={`${testid}-input`} />
        </label>
        <input
          type="url"
          value={isData ? "" : value}
          placeholder="…or paste image URL"
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-ink-900 border border-white/15 text-white px-3 py-2 font-body text-sm focus:border-pfc-gold focus:outline-none"
          data-testid={`${testid}-url`}
        />
      </div>
    </div>
  );
}
